import { themeTokens } from '@dma/config';

/**
 * Converts the typed design tokens (packages/config) into CSS custom properties,
 * so component-level CSS files can reference `var(--dma-color-brand-primary)`
 * without duplicating raw values.
 */
export function themeTokensToCssVariables(): Record<string, string> {
  return {
    '--dma-color-background': themeTokens.colors.background,
    '--dma-color-surface': themeTokens.colors.surface,
    '--dma-color-border': themeTokens.colors.border,
    '--dma-color-text-primary': themeTokens.colors.textPrimary,
    '--dma-color-text-secondary': themeTokens.colors.textSecondary,
    '--dma-color-brand-primary': themeTokens.colors.brandPrimary,
    '--dma-color-brand-primary-dark': themeTokens.colors.brandPrimaryDark,
    '--dma-color-brand-accent': themeTokens.colors.brandAccent,
    '--dma-color-brand-accent-dark': themeTokens.colors.brandAccentDark,
    '--dma-color-surface-dark': themeTokens.colors.surfaceDark,
    '--dma-color-text-on-dark': themeTokens.colors.textOnDark,
    '--dma-font-family-base': themeTokens.typography.fontFamilyBase,
    '--dma-font-size-base': themeTokens.typography.fontSizeBase,
    '--dma-line-height-base': String(themeTokens.typography.lineHeightBase),
    '--dma-spacing-xs': themeTokens.spacing.xs,
    '--dma-spacing-sm': themeTokens.spacing.sm,
    '--dma-spacing-md': themeTokens.spacing.md,
    '--dma-spacing-lg': themeTokens.spacing.lg,
    '--dma-spacing-xl': themeTokens.spacing.xl,
    '--dma-radius-sm': themeTokens.radii.sm,
    '--dma-radius-md': themeTokens.radii.md,
    '--dma-radius-lg': themeTokens.radii.lg
  };
}

/**
 * Actually applies the tokens above to an element's inline style (the
 * document root by default) as real CSS custom properties.
 *
 * `themeTokensToCssVariables()` alone only builds the JS object — nothing
 * in the app ever called it, so every `var(--dma-...)` reference across
 * every component's CSS resolved to nothing (an unresolved `var()` makes
 * the whole declaration invalid, not "use some default"), which collapsed
 * spacing/colors/radii site-wide. Call this once, as early as possible
 * (see apps/website/src/app/main.tsx), before the first paint.
 */
export function applyThemeTokens(target: HTMLElement = document.documentElement): void {
  const variables = themeTokensToCssVariables();
  for (const [property, value] of Object.entries(variables)) {
    target.style.setProperty(property, value);
  }
}
